import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, User, MapPin, Wallet, FileText, Phone, CheckCircle, AlertCircle, ArrowRight, Loader2, Upload, X, File, Users, Briefcase, CreditCard, Receipt } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import NPA_LOCALITES from '../data/npaSwiss';
import { loadUserDataCache, saveUserDataCache } from '../utils/userDataCache';
import logger from '../utils/logger';
import { compressImage, formatFileSize } from '../utils/imageCompression';
import SEO, { SEOPages } from '../components/SEO';

// LocalStorage key for form data cache
const FORM_CACHE_KEY = 'atom_credit_form_cache';

// Données constantes
const NATIONALITES = [
  "Suisse", "France", "Italie", "Portugal", "Allemagne", "Espagne", "Kosovo", "Serbie", 
  "Macédoine du Nord", "Turquie", "Croatie", "Bosnie-Herzégovine", "Autriche", "Royaume-Uni",
  "Albanie", "Algérie", "Belgique", "Brésil", "Bulgarie", "Cameroun", "Canada", "Chine", 
  "Colombie", "Congo (RDC)", "Côte d'Ivoire", "Égypte", "Érythrée", "États-Unis", "Éthiopie",
  "Grèce", "Hongrie", "Inde", "Irak", "Iran", "Irlande", "Liban", "Libye", "Luxembourg",
  "Maroc", "Mexique", "Moldavie", "Monténégro", "Nigeria", "Pakistan", "Pays-Bas", "Pologne",
  "Roumanie", "Russie", "Slovaquie", "Slovénie", "Somalie", "Sri Lanka", "Suède", "Syrie",
  "Tchéquie", "Thaïlande", "Tunisie", "Ukraine", "Vietnam", "Autre"
].sort((a, b) => {
  if (a === "Suisse") return -1;
  if (b === "Suisse") return 1;
  if (a === "Autre") return 1;
  if (b === "Autre") return -1;
  return a.localeCompare(b, 'fr');
});

const ETATS_CIVILS = [
  "Célibataire", "Marié(e)", "Divorcé(e)", "Séparé(e)", "Veuf(ve)", "Partenariat enregistré"
];

const SITUATIONS_LOGEMENT = [
  { value: "locataire", label: "Locataire" },
  { value: "copropriete", label: "Copropriété (appartement)" },
  { value: "proprietaire", label: "Propriétaire (maison individuelle)" },
  { value: "colocation", label: "Colocation / Loyer partagé" },
  { value: "logement_fonction", label: "Logement de fonction" },
  { value: "chez_parents", label: "Chez les parents" },
  { value: "autre", label: "Autre" }
];

const NOMBRE_VERSEMENTS = [
  { value: 11, label: "11 versements" },
  { value: 12, label: "12 versements" },
  { value: 13, label: "13 versements (13e salaire)" },
  { value: 14, label: "14 versements" }
];

const TITRES_SEJOUR = [
  { value: "Suisse", label: "Nationalité Suisse" },
  { value: "C", label: "Permis C (établissement)" },
  { value: "B", label: "Permis B (séjour)" },
  { value: "G", label: "Permis G (frontalier)" },
  { value: "L", label: "Permis L (court séjour)" },
  { value: "Légitimation", label: "Carte de légitimation" },
  { value: "Autre", label: "Autre" }
];

const MOYENS_TRANSPORT = [
  { value: "transports_publics", label: "Transports publics (train, bus, tram)" },
  { value: "voiture", label: "Voiture" },
  { value: "moto_scooter", label: "Moto / Scooter" },
  { value: "velo", label: "Vélo / Trottinette" },
  { value: "a_pied", label: "À pied" },
  { value: "mixte", label: "Mixte (transports publics + voiture)" }
];

const PROFILS = [
  { value: "salarie", label: "Salarié(e) en Suisse" },
  { value: "independant", label: "Indépendant(e)" },
  { value: "frontalier", label: "Frontalier (domicilié à l'étranger)" },
  { value: "rentier", label: "Rentier AVS/AI" }
];

const DOCUMENTS_PAR_PROFIL = {
  salarie: [
    { key: "piece_identite", label: "Pièce d'identité / Permis de séjour (recto-verso)", required: true },
    { key: "fiches_salaire", label: "3 dernières fiches de salaire", required: true },
    { key: "bail_loyer", label: "Bail à loyer ou contrat hypothèque (avec charges)", required: false },
    { key: "facture_lamal", label: "Dernière facture assurance maladie LAMal", required: false }
  ],
  independant: [
    { key: "piece_identite", label: "Pièce d'identité / Permis C (recto-verso)", required: true },
    { key: "taxation_fiscale", label: "Dernière taxation fiscale complète", required: true },
    { key: "bilan_resultat", label: "Bilan et compte de résultat (2 derniers exercices)", required: false },
    { key: "bail_loyer", label: "Bail à loyer ou contrat hypothèque (avec charges)", required: false },
    { key: "facture_lamal", label: "Dernière facture assurance maladie LAMal", required: false }
  ],
  frontalier: [
    { key: "piece_identite", label: "Pièce d'identité / Permis G (recto-verso)", required: true },
    { key: "fiches_salaire", label: "3 dernières fiches de salaire", required: true },
    { key: "releves_bancaires", label: "3 derniers relevés bancaires (CH + étranger)", required: true },
    { key: "facture_electricite", label: "Dernière facture d'électricité (domicile étranger)", required: false }
  ],
  rentier: [
    { key: "piece_identite", label: "Pièce d'identité / Permis de séjour (recto-verso)", required: true },
    { key: "attestation_rentes", label: "Attestation de rentes AVS/AI", required: true },
    { key: "releves_bancaires", label: "3 derniers relevés de compte", required: true },
    { key: "bail_loyer", label: "Bail à loyer ou contrat hypothèque (avec charges)", required: false },
    { key: "facture_lamal", label: "Dernière facture assurance maladie LAMal", required: false }
  ]
};

// Documents supplémentaires selon la situation
const DOCUMENTS_CONJOINT = [
  { key: "conjoint_identite", label: "Pièce d'identité du conjoint / partenaire (recto-verso)", required: true },
  { key: "conjoint_salaire_doc", label: "Certificat de salaire ou fiches de salaire du conjoint", required: true }
];

const DOCUMENTS_CONSOLIDATION = [
  { key: "contrat_credit_existant", label: "Copie du contrat de crédit existant à reprendre", required: true }
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB max par fichier (sera compressé à ~1MB)
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/tiff', 'image/gif', 'image/bmp'];

// Default form values
const DEFAULT_FORM_DATA = {
  // Crédit
  montant: 50000,
  duree_mois: 48,
  garantie_credit: null,
  
  // Identité
  titre: '',
  nom: '',
  prenom: '',
  date_naissance: '',
  
  // Adresse
  rue: '',
  npa_localite: '',
  situation_logement: '',
  loyer_mensuel: '',
  en_menage_depuis: '',
  
  // Ancienne adresse (si < 6 mois à l'adresse actuelle)
  ancienne_rue: '',
  ancienne_npa_localite: '',
  ancienne_pays: 'CH',
  
  // Situation personnelle
  etat_civil: '',
  nationalite: '',
  titre_sejour: '',
  en_suisse_depuis: '',
  
  // Enfants
  a_enfants: null,
  nombre_enfants: '',
  enfants_annees_naissance: [],
  
  // Emploi
  profil: '',
  employeur: '',
  employeur_code_postal: '',
  employeur_localite: '',
  profession: '',
  en_poste_depuis: '',
  salaire_net: '',
  nombre_versements_salaire: '',
  
  // Revenus et dépenses détaillées
  revenus_supplementaires: '',
  moyen_transport: '',
  frais_trajets_travail: '',
  depenses_repas_exterieur: '',
  pensions_alimentaires: '',
  primes_assurance_maladie: '',
  impots_mensuels: '',
  autres_depenses_mensuelles: '',
  mensualites_leasing: '',
  
  // Conjoint (si marié) - Cembra compatible
  conjoint_titre: '',
  conjoint_nom: '',
  conjoint_prenom: '',
  conjoint_email: '',
  conjoint_date_naissance: '',
  conjoint_nationalite: '',
  conjoint_titre_sejour: '',
  conjoint_en_suisse_depuis: '',
  conjoint_profil: '',
  conjoint_employeur: '',
  conjoint_employeur_code_postal: '',
  conjoint_employeur_localite: '',
  conjoint_en_poste_depuis: '',
  conjoint_salaire: '',
  conjoint_nombre_versements: '',
  conjoint_revenus_supplementaires: '',
  conjoint_pensions_alimentaires: '',
  conjoint_autres_dettes: '',
  
  // Crédit existant
  credit_existant: null,
  credit_existant_montant: '',
  
  // Finances
  poursuites: 'aucune',
  montant_poursuites: '',
  iban: '',
  
  // Contact
  telephone: '',
  email: '',
  commentaire: '',
  conseiller_recommandant: '',
  
  // Consentements (never cached for security)
  accept_conditions: false,
  accept_traitement: false,
  confirmation_exactitude_donnees: false,
  conjoint_accept_conditions: false
};

// Load cached form data from localStorage
const loadCachedFormData = () => {
  try {
    // First load shared user data
    const sharedUserData = loadUserDataCache();
    
    // Then load form-specific cache
    const cached = localStorage.getItem(FORM_CACHE_KEY);
    const formCache = cached ? JSON.parse(cached) : {};
    
    // Merge: defaults < shared user data < form-specific cache
    return {
      ...DEFAULT_FORM_DATA,
      // Apply shared user data
      nom: sharedUserData.nom || DEFAULT_FORM_DATA.nom,
      prenom: sharedUserData.prenom || DEFAULT_FORM_DATA.prenom,
      email: sharedUserData.email || DEFAULT_FORM_DATA.email,
      telephone: sharedUserData.telephone || DEFAULT_FORM_DATA.telephone,
      date_naissance: sharedUserData.date_naissance || sharedUserData.dateNaissance || DEFAULT_FORM_DATA.date_naissance,
      rue: sharedUserData.adresse || DEFAULT_FORM_DATA.rue,
      npa_localite: sharedUserData.npa_localite || (sharedUserData.npa && sharedUserData.ville ? `${sharedUserData.npa} ${sharedUserData.ville}` : DEFAULT_FORM_DATA.npa_localite),
      // Then apply form-specific cache (overrides shared data)
      ...formCache,
      // Always reset consents for security
      accept_conditions: false,
      accept_traitement: false,
      confirmation_exactitude_donnees: false,
      conjoint_accept_conditions: false
    };
  } catch (e) {
    logger.warn('Erreur lors du chargement du cache:', e);
  }
  return DEFAULT_FORM_DATA;
};

// Pays pour l'ancienne adresse (Cembra previousAddress)
const PAYS = [
  { value: "CH", label: "Suisse" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Allemagne" },
  { value: "IT", label: "Italie" },
  { value: "AT", label: "Autriche" },
  { value: "LI", label: "Liechtenstein" },
  { value: "PT", label: "Portugal" },
  { value: "ES", label: "Espagne" },
  { value: "BE", label: "Belgique" },
  { value: "LU", label: "Luxembourg" },
  { value: "NL", label: "Pays-Bas" },
  { value: "GB", label: "Royaume-Uni" },
  { value: "OTHER", label: "Autre" }
];

/**
 * Vérifie si la date "en ménage depuis" est inférieure à 6 mois par rapport à aujourd'hui.
 * @param {string} enMenageDepuis - Format YYYY-MM
 * @returns {boolean} true si < 6 mois
 */
const isLessThan6Months = (enMenageDepuis) => {
  if (!enMenageDepuis || !enMenageDepuis.includes('-') || enMenageDepuis.length !== 7) return false;
  const [year, month] = enMenageDepuis.split('-').map(Number);
  if (!year || !month || month < 1 || month > 12) return false;
  const now = new Date();
  const moveDate = new Date(year, month - 1, 1);
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  return moveDate > sixMonthsAgo;
};

const DemandeCredit = () => {
  const [formData, setFormData] = useState(loadCachedFormData);

  const [uploadedFiles, setUploadedFiles] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploadStatus, setUploadStatus] = useState({ show: false, message: '', attempt: 0 });

  // Save form data to localStorage when it changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        // Don't cache consent fields for security
        const dataToCache = { ...formData };
        delete dataToCache.accept_conditions;
        delete dataToCache.accept_traitement;
        delete dataToCache.confirmation_exactitude_donnees;
        delete dataToCache.conjoint_accept_conditions;
        
        // Save to form-specific cache
        localStorage.setItem(FORM_CACHE_KEY, JSON.stringify(dataToCache));
        
        // Also save to shared user cache for other forms
        saveUserDataCache({
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone,
          date_naissance: formData.date_naissance,
          adresse: formData.rue,
          npa_localite: formData.npa_localite
        });
      } catch (e) {
        logger.warn('Erreur lors de la sauvegarde du cache:', e);
      }
    }, 500); // Debounce 500ms
    
    return () => clearTimeout(timeoutId);
  }, [formData]);

  // Calcul mensualité
  const mensualite = useMemo(() => {
    const { montant, duree_mois } = formData;
    const taux = 5.95 / 100 / 12;
    const n = duree_mois;
    if (n <= 0 || montant <= 0) return 0;
    return montant * (taux * Math.pow(1 + taux, n)) / (Math.pow(1 + taux, n) - 1);
  }, [formData.montant, formData.duree_mois]);

  // Documents requis selon profil + situation
  const documentsRequis = useMemo(() => {
    let docs = [...(DOCUMENTS_PAR_PROFIL[formData.profil] || [])];
    // Ajouter docs conjoint si marié/partenariat
    if (formData.etat_civil === 'Marié(e)' || formData.etat_civil === 'Partenariat enregistré') {
      docs = [...docs, ...DOCUMENTS_CONJOINT];
    }
    // Ajouter doc consolidation si crédit existant
    if (formData.credit_existant === true) {
      docs = [...docs, ...DOCUMENTS_CONSOLIDATION];
    }
    return docs;
  }, [formData.profil, formData.etat_civil, formData.credit_existant]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Quand le nombre d'enfants change, ajuster le tableau des années
      if (field === 'nombre_enfants') {
        const count = parseInt(value) || 0;
        const current = prev.enfants_annees_naissance || [];
        if (count > current.length) {
          updated.enfants_annees_naissance = [...current, ...Array(count - current.length).fill('')];
        } else {
          updated.enfants_annees_naissance = current.slice(0, count);
        }
      }
      // Reset enfants data when "non" selected
      if (field === 'a_enfants' && value === false) {
        updated.nombre_enfants = '';
        updated.enfants_annees_naissance = [];
      }
      return updated;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleChildYearChange = (index, year) => {
    setFormData(prev => {
      const years = [...(prev.enfants_annees_naissance || [])];
      years[index] = year;
      return { ...prev, enfants_annees_naissance: years };
    });
  };

  const handleFileUpload = useCallback((docKey, files) => {
    if (!files || files.length === 0) return;
    
    const newFiles = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (!ALLOWED_TYPES.includes(file.type)) {
        setErrors(prev => ({ ...prev, [docKey]: "Format non accepté (PDF, JPG, PNG, TIF, GIF, BMP)" }));
        return;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({ ...prev, [docKey]: "Fichier trop volumineux (max 5 MB)" }));
        return;
      }
      
      newFiles.push(file);
    }
    
    // Ajouter aux fichiers existants pour cette catégorie
    setUploadedFiles(prev => ({
      ...prev,
      [docKey]: [...(prev[docKey] || []), ...newFiles]
    }));
    setErrors(prev => ({ ...prev, [docKey]: null }));
  }, []);

  const removeFile = useCallback((docKey, fileIndex) => {
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      if (newFiles[docKey]) {
        newFiles[docKey] = newFiles[docKey].filter((_, idx) => idx !== fileIndex);
        if (newFiles[docKey].length === 0) {
          delete newFiles[docKey];
        }
      }
      return newFiles;
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};
    const isEmpty = (value) => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
    
    // Identité
    if (!formData.titre) newErrors.titre = "Requis";
    if (!formData.nom.trim()) newErrors.nom = "Requis";
    if (!formData.prenom.trim()) newErrors.prenom = "Requis";
    if (!formData.date_naissance) newErrors.date_naissance = "Requis";
    
    // Adresse
    if (!formData.rue.trim()) newErrors.rue = "Requis";
    if (!formData.npa_localite.trim()) newErrors.npa_localite = "Requis";
    if (!formData.situation_logement) newErrors.situation_logement = "Requis";
    if (!formData.loyer_mensuel && formData.situation_logement && formData.situation_logement !== 'chez_parents') newErrors.loyer_mensuel = "Requis";
    if (!formData.en_menage_depuis) newErrors.en_menage_depuis = "Requis";
    
    // Ancienne adresse (obligatoire si < 6 mois à l'adresse actuelle)
    if (isLessThan6Months(formData.en_menage_depuis)) {
      if (!formData.ancienne_rue?.trim()) newErrors.ancienne_rue = "Requis";
      if (!formData.ancienne_npa_localite?.trim()) newErrors.ancienne_npa_localite = "Requis";
      if (!formData.ancienne_pays) newErrors.ancienne_pays = "Requis";
    }
    
    // Famille
    if (!formData.etat_civil) newErrors.etat_civil = "Requis";
    if (formData.a_enfants === null) newErrors.a_enfants = "Requis";
    if (formData.a_enfants === true && !formData.nombre_enfants) newErrors.nombre_enfants = "Requis";

    // Couple : e-mail du conjoint requis (validation de son consentement par e-mail)
    const isCouple = formData.etat_civil === 'Marié(e)' || formData.etat_civil === 'Partenariat enregistré';
    if (isCouple) {
      if (!formData.conjoint_email?.trim()) {
        newErrors.conjoint_email = "Requis — votre conjoint recevra un e-mail pour valider son consentement";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.conjoint_email.trim())) {
        newErrors.conjoint_email = "Adresse e-mail invalide";
      }
      if (!formData.conjoint_accept_conditions) newErrors.conjoint_accept_conditions = "Requis";
    }
    
    // Emploi
    if (!formData.profil) newErrors.profil = "Veuillez sélectionner votre profil";
    if (!formData.employeur?.trim()) newErrors.employeur = "Requis";
    if (!formData.profession?.trim()) newErrors.profession = "Requis";
    if (!formData.employeur_code_postal?.trim()) newErrors.employeur_code_postal = "Requis";
    if (!formData.employeur_localite?.trim()) newErrors.employeur_localite = "Requis";
    if (!formData.en_poste_depuis) newErrors.en_poste_depuis = "Requis";
    if (!formData.salaire_net || formData.salaire_net < 1000) newErrors.salaire_net = "Requis";
    if (!formData.nombre_versements_salaire) newErrors.nombre_versements_salaire = "Requis";
    if (!formData.moyen_transport) newErrors.moyen_transport = "Requis";
    if (isEmpty(formData.frais_trajets_travail)) newErrors.frais_trajets_travail = "Requis";
    if (isEmpty(formData.depenses_repas_exterieur)) newErrors.depenses_repas_exterieur = "Requis";
    if (isEmpty(formData.pensions_alimentaires)) newErrors.pensions_alimentaires = "Requis";
    if (isEmpty(formData.primes_assurance_maladie)) newErrors.primes_assurance_maladie = "Requis";
    if (isEmpty(formData.impots_mensuels)) newErrors.impots_mensuels = "Requis";
    if (isEmpty(formData.mensualites_leasing)) newErrors.mensualites_leasing = "Requis";
    if (isEmpty(formData.autres_depenses_mensuelles)) newErrors.autres_depenses_mensuelles = "Requis";
    
    // Nationalité
    if (!formData.nationalite) newErrors.nationalite = "Requis";
    // Titre de séjour requis uniquement si nationalité non-suisse
    if (formData.nationalite !== 'Suisse' && !formData.titre_sejour) newErrors.titre_sejour = "Requis";
    
    // Contact
    if (!formData.telephone.trim()) newErrors.telephone = "Requis";
    if (!formData.email.trim()) newErrors.email = "Requis";
    
    // Consentements
    if (!formData.accept_conditions) newErrors.accept_conditions = "Requis";
    if (!formData.accept_traitement) newErrors.accept_traitement = "Requis";
    if (!formData.confirmation_exactitude_donnees) newErrors.confirmation_exactitude_donnees = "Requis";
    
    // Poursuites
    if (formData.poursuites !== 'aucune' && !formData.montant_poursuites) {
      newErrors.montant_poursuites = "Requis";
    }

    if (isEmpty(formData.iban)) {
      newErrors.iban = "Requis";
    } else if (/\s/.test(formData.iban)) {
      newErrors.iban = "Veuillez coller votre IBAN sans espace (21 caractères), par exemple : CH8000000000000000000.";
    } else if (!/^CH[0-9A-Z]{19}$/.test(formData.iban.toUpperCase())) {
      newErrors.iban = "IBAN invalide : veuillez saisir 21 caractères collés, par exemple : CH8000000000000000000.";
    }

    // Vérifier documents requis
    documentsRequis.filter(d => d.required).forEach(doc => {
      if (!uploadedFiles[doc.key] || uploadedFiles[doc.key].length === 0) {
        newErrors[doc.key] = "Document requis";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to the first error field
      setTimeout(() => {
        const firstErrorElement = document.querySelector('[data-error="true"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Flash animation to draw attention
          firstErrorElement.classList.add('animate-pulse');
          setTimeout(() => firstErrorElement.classList.remove('animate-pulse'), 2000);
        }
      }, 100);
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const formDataToSend = new FormData();
      
      // Convertir nombre_versements_salaire en treizieme_salaire pour compatibilité backend
      const dataToSubmit = {
        ...formData,
        montant: parseInt(formData.montant),
        duree_mois: parseInt(formData.duree_mois),
        salaire_net: parseInt(formData.salaire_net),
        loyer_mensuel: formData.loyer_mensuel ? parseInt(formData.loyer_mensuel) : null,
        nombre_versements_salaire: formData.nombre_versements_salaire ? parseInt(formData.nombre_versements_salaire) : null,
        treizieme_salaire: formData.nombre_versements_salaire >= 13,
        revenus_supplementaires: formData.revenus_supplementaires ? parseInt(formData.revenus_supplementaires) : null,
        frais_trajets_travail: formData.frais_trajets_travail ? parseInt(formData.frais_trajets_travail) : null,
        depenses_repas_exterieur: formData.depenses_repas_exterieur ? parseInt(formData.depenses_repas_exterieur) : null,
        pensions_alimentaires: formData.pensions_alimentaires ? parseInt(formData.pensions_alimentaires) : null,
        primes_assurance_maladie: formData.primes_assurance_maladie ? parseInt(formData.primes_assurance_maladie) : null,
        impots_mensuels: formData.impots_mensuels ? parseInt(formData.impots_mensuels) : null,
        autres_depenses_mensuelles: formData.autres_depenses_mensuelles ? parseInt(formData.autres_depenses_mensuelles) : null,
        mensualites_leasing: formData.mensualites_leasing ? parseInt(formData.mensualites_leasing) : null,
        montant_poursuites: formData.montant_poursuites ? parseInt(formData.montant_poursuites) : null,
        credit_existant_montant: formData.credit_existant_montant ? parseInt(formData.credit_existant_montant) : null,
        conjoint_salaire: formData.conjoint_salaire ? parseInt(formData.conjoint_salaire) : null,
        conjoint_nombre_versements: formData.conjoint_nombre_versements ? parseInt(formData.conjoint_nombre_versements) : null,
        conjoint_revenus_supplementaires: formData.conjoint_revenus_supplementaires ? parseInt(formData.conjoint_revenus_supplementaires) : null,
        conjoint_pensions_alimentaires: formData.conjoint_pensions_alimentaires ? parseInt(formData.conjoint_pensions_alimentaires) : null,
        conjoint_autres_dettes: formData.conjoint_autres_dettes ? parseInt(formData.conjoint_autres_dettes) : null,
        nombre_enfants: formData.nombre_enfants ? parseInt(formData.nombre_enfants) : null,
        enfants_annees_naissance: (formData.enfants_annees_naissance || []).filter(y => y).map(y => parseInt(y))
      };
      
      formDataToSend.append('form_data', JSON.stringify(dataToSubmit));
      
      // Compress and add files (with progress)
      const totalFiles = Object.values(uploadedFiles).flat().length;
      setUploadStatus({ 
        show: true, 
        message: `Compression des ${totalFiles} document(s)...`, 
        attempt: 0 
      });
      
      const fileTypes = [];
      let processedFiles = 0;
      let totalOriginalSize = 0;
      let totalCompressedSize = 0;
      
      logger.info(`Début compression de ${totalFiles} fichiers...`);
      
      for (const [docKey, files] of Object.entries(uploadedFiles)) {
        const docInfo = documentsRequis.find(d => d.key === docKey);
        const label = docInfo?.label || docKey;
        
        for (let idx = 0; idx < files.length; idx++) {
          const file = files[idx];
          totalOriginalSize += file.size;
          
          // Compress ALL images before upload (regardless of size)
          let fileToUpload = file;
          if (file.type.startsWith('image/')) {
            setUploadStatus({ 
              show: true, 
              message: `Compression image ${processedFiles + 1}/${totalFiles}...`, 
              attempt: 0 
            });
            fileToUpload = await compressImage(file, {
              maxWidth: 1200,
              maxHeight: 1200,
              quality: 0.5,
              maxSize: 500 * 1024 // 500KB max per image for safer upload
            });
            logger.info(`Image ${file.name}: ${formatFileSize(file.size)} → ${formatFileSize(fileToUpload.size)}`);
          } else {
            // PDF or other file - no compression
            logger.info(`Fichier ${file.name} (${file.type}): ${formatFileSize(file.size)} (non compressé)`);
          }
          
          totalCompressedSize += fileToUpload.size;
          formDataToSend.append('files', fileToUpload);
          fileTypes.push(files.length > 1 ? `${label} (${idx + 1}/${files.length})` : label);
          processedFiles++;
        }
      }
      
      logger.info(`Compression terminée: ${formatFileSize(totalOriginalSize)} → ${formatFileSize(totalCompressedSize)}`);
      
      formDataToSend.append('file_types', JSON.stringify(fileTypes));
      
      // Log compression stats
      if (totalOriginalSize !== totalCompressedSize) {
        const reduction = ((1 - totalCompressedSize / totalOriginalSize) * 100).toFixed(0);
        logger.info(`Compression: ${formatFileSize(totalOriginalSize)} → ${formatFileSize(totalCompressedSize)} (-${reduction}%)`);
      }
      
      // Show upload popup
      setUploadStatus({ 
        show: true, 
        message: `Envoi de votre demande (${formatFileSize(totalCompressedSize)})...`, 
        attempt: 1 
      });
      
      // Retry logic for cold start and intermittent errors (5 attempts with increasing delays)
      const MAX_ATTEMPTS = 5;
      let lastError = null;
      
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          setUploadStatus({ 
            show: true, 
            message: attempt === 1 
              ? `Envoi de ${totalFiles} document(s) (${formatFileSize(totalCompressedSize)})...`
              : `Connexion au serveur... (${attempt}/${MAX_ATTEMPTS})`, 
            attempt 
          });
          
          const response = await axios.post(
            `${API_BASE_URL}/credit/demande`,
            formDataToSend,
            { 
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 120000 // 120 seconds timeout for file uploads
            }
          );

          setUploadStatus({ show: false, message: '', attempt: 0 });
          setSubmitResult({
            success: true,
            message: response.data.message,
            reference: response.data.reference,
            mensualite: response.data.mensualite_estimee,
            documents: response.data.documents_recus
          });

          window.scrollTo({ top: 0, behavior: 'smooth' });
          logger.info(`Demande crédit envoyée avec succès après ${attempt} tentative(s)`);
          return; // Success, exit the function
        } catch (error) {
          lastError = error;
          const statusCode = error.response?.status;
          const errorMessage = error.response?.data?.detail || error.message || '';
          
          // Handle 413 (Payload Too Large) - don't retry, show specific message
          if (statusCode === 413) {
            logger.error(`Fichiers trop volumineux (413): ${formatFileSize(totalCompressedSize)}`);
            setUploadStatus({ show: false, message: '', attempt: 0 });
            setSubmitResult({
              success: false,
              message: `Les fichiers sont trop volumineux (${formatFileSize(totalCompressedSize)}). Veuillez réduire la taille des images ou envoyer moins de documents.`
            });
            return;
          }
          
          // Determine if error is retryable (cold start, auth issues, network)
          const isRetryableStatus = statusCode === 401 || statusCode === 404 || statusCode === 502 || statusCode === 503 || statusCode === 504;
          const isRetryableError = errorMessage.includes('authentification') || 
                                   errorMessage.includes('Token') ||
                                   errorMessage.includes('Network') ||
                                   errorMessage.includes('CERT') ||
                                   errorMessage.includes('SSL') ||
                                   errorMessage.includes('Unauthorized') ||
                                   error.code === 'ECONNABORTED' ||
                                   error.code === 'ERR_NETWORK' ||
                                   !error.response; // No response = network/SSL issue
          
          const shouldRetry = isRetryableStatus || isRetryableError;
          
          if (attempt < MAX_ATTEMPTS && shouldRetry) {
            // Progressive delay: 1s, 2s, 3s, 4s for cold start recovery
            const delay = attempt * 1000;
            logger.warn(`Tentative ${attempt}/${MAX_ATTEMPTS} échouée (${statusCode || 'network'}), retry dans ${delay/1000}s...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
          
          // Don't retry for validation errors (400) or other client errors
          if (statusCode === 400) {
            logger.error(`Erreur de validation: ${errorMessage}`);
          }
          break;
        }
      }
      
      // If we get here, all attempts failed
      logger.error(`Toutes les ${MAX_ATTEMPTS} tentatives ont échoué pour /api/credit/demande`);
      setUploadStatus({ show: false, message: '', attempt: 0 });
      setSubmitResult({
        success: false,
        message: lastError?.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer."
      });
    } catch (error) {
      setUploadStatus({ show: false, message: '', attempt: 0 });
      setSubmitResult({
        success: false,
        message: error.response?.data?.detail || "Une erreur est survenue."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Page de succès
  if (submitResult?.success) {
    return (
      <div className="pt-20 min-h-screen" data-testid="credit-success-page">
        <div className="container py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="glass p-12 rounded-3xl">
              <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">Demande envoyée avec succès !</h1>
              <p className="text-gray-300 mb-6">
                Merci pour votre confiance. Notre équipe analysera votre demande et vous contactera sous 24h.
              </p>
              <div className="glass p-6 rounded-xl mb-8">
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div>
                    <p className="text-gray-400 text-sm">Référence</p>
                    <p className="text-xl font-bold text-blue-400" data-testid="credit-reference">{submitResult.reference}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Mensualité estimée</p>
                    <p className="text-xl font-bold text-white">CHF {submitResult.mensualite?.toFixed(2)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-400 text-sm">Documents reçus</p>
                    <p className="text-lg font-semibold text-green-400">{submitResult.documents} fichier(s)</p>
                  </div>
                </div>
              </div>
              <Link to="/credits-prives" className="btn-glass btn-primary inline-flex items-center" data-testid="back-to-credits-btn">
                Retour aux crédits privés
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="demande-credit-page">
      <SEO {...SEOPages.demandeCredit} />
      {/* Error Popup Modal */}
      {submitResult?.success === false && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="error-popup">
          <div className="glass p-8 rounded-2xl max-w-md mx-4 text-center border border-red-500/30">
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
              <AlertCircle className="text-white" size={32} />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Erreur d'envoi</h3>
            <p className="text-gray-300 mb-6">{submitResult.message}</p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setSubmitResult(null);
                  // Retry submission
                  document.querySelector('form')?.requestSubmit();
                }}
                className="w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl transition-all"
                data-testid="retry-button"
              >
                Réessayer l'envoi
              </button>
              <button
                onClick={() => setSubmitResult(null)}
                className="w-full py-3 px-6 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all"
                data-testid="close-error-button"
              >
                Fermer et corriger
              </button>
            </div>
            <p className="text-gray-500 text-sm mt-4">
              Si le problème persiste, contactez-nous au <a href="tel:+41327242777" className="text-blue-400 hover:underline">032 724 27 77</a>
            </p>
          </div>
        </div>
      )}

      {/* Upload Progress Popup */}
      {uploadStatus.show && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="glass p-8 rounded-2xl max-w-md mx-4 text-center">
            <div className="w-16 h-16 mx-auto mb-6 relative">
              <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <Upload className="absolute inset-0 m-auto text-blue-400" size={28} />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Envoi en cours...</h3>
            <p className="text-gray-300 mb-4">{uploadStatus.message}</p>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((dot) => (
                <div 
                  key={dot}
                  className={`w-2 h-2 rounded-full transition-colors ${uploadStatus.attempt >= dot ? 'bg-blue-500' : 'bg-gray-600'}`}
                />
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-4">
              Veuillez patienter, ne fermez pas cette page.
            </p>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900/50 to-slate-900"></div>
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold heading-display text-white mb-4">
              Demande de <span className="text-blue-300">Crédit Privé</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Remplissez le formulaire et uploadez vos documents pour une réponse sous 24h
            </p>
            
            {/* Calculateur */}
            <div className="glass p-6 rounded-2xl inline-block">
              <div className="flex items-center justify-center gap-8 flex-wrap">
                <div className="text-center">
                  <p className="text-gray-400 text-sm">Montant</p>
                  <p className="text-2xl font-bold text-white">CHF {formData.montant.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 text-sm">Durée</p>
                  <p className="text-2xl font-bold text-white">{formData.duree_mois} mois</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 text-sm">Mensualité dès</p>
                  <p className="text-2xl font-bold text-green-400" data-testid="mensualite-display">CHF {mensualite.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section className="py-16">
        <div className="container">
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto" data-testid="credit-form">
            
            {/* Section Crédit */}
            <div className="glass p-8 rounded-2xl mb-8">
              <SectionHeader icon={Calculator} title="Votre crédit" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-300 mb-2">Montant souhaité</label>
                  <input
                    type="range" min="5000" max="250000" step="1000"
                    value={formData.montant}
                    onChange={(e) => handleChange('montant', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider mb-2"
                    data-testid="montant-slider"
                  />
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>CHF 5'000</span>
                    <span className="text-xl font-bold text-white">CHF {formData.montant.toLocaleString()}</span>
                    <span>CHF 250'000</span>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Durée souhaitée</label>
                  <select
                    value={formData.duree_mois}
                    onChange={(e) => handleChange('duree_mois', parseInt(e.target.value))}
                    className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white"
                    data-testid="duree-select"
                  >
                    {[12, 24, 36, 48, 60, 72, 84].map(m => (
                      <option key={m} value={m}>{m} mois ({m/12} {m === 12 ? 'an' : 'ans'})</option>
                    ))}
                  </select>
                </div>
                
                {/* Garantie de crédit */}
                <div className="md:col-span-2">
                  <label className="block text-gray-300 mb-3">Garantie de crédit</label>
                  <div className="flex gap-4">
                    <label 
                      className={`flex-1 p-4 rounded-xl cursor-pointer border-2 transition-all text-center ${
                        formData.garantie_credit === true 
                          ? 'border-green-500 bg-green-500/20 text-white' 
                          : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                      }`}>
                      <input type="radio" name="garantie_credit"
                        checked={formData.garantie_credit === true}
                        onChange={() => handleChange('garantie_credit', true)}
                        className="sr-only"
                        data-testid="garantie-oui" />
                      <span className="font-semibold">Oui</span>
                      <p className="text-xs mt-1 opacity-70">Je souhaite une garantie</p>
                    </label>
                    <label 
                      className={`flex-1 p-4 rounded-xl cursor-pointer border-2 transition-all text-center ${
                        formData.garantie_credit === false 
                          ? 'border-blue-500 bg-blue-500/20 text-white' 
                          : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                      }`}>
                      <input type="radio" name="garantie_credit"
                        checked={formData.garantie_credit === false}
                        onChange={() => handleChange('garantie_credit', false)}
                        className="sr-only"
                        data-testid="garantie-non" />
                      <span className="font-semibold">Non</span>
                      <p className="text-xs mt-1 opacity-70">Sans garantie</p>
                    </label>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">
                    La garantie de crédit protège vos proches en cas d'incapacité de remboursement.
                  </p>
                </div>
              </div>
            </div>

            {/* Section Identité */}
            <div className="glass p-8 rounded-2xl mb-8">
              <SectionHeader icon={User} title="Identité" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div data-error={errors.titre ? 'true' : 'false'}>
                  <label className={`block mb-2 font-medium ${errors.titre ? 'text-red-400' : 'text-gray-300'}`}>Titre *</label>
                  <div className={`flex gap-4 p-3 rounded-xl border-2 transition-all ${
                    errors.titre 
                      ? 'border-red-500 ring-2 ring-red-500/20' 
                      : 'border-gray-700 bg-gray-800/50'
                  }`}>
                    {['Monsieur', 'Madame'].map(t => (
                      <label key={t} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="titre" value={t}
                          checked={formData.titre === t}
                          onChange={(e) => handleChange('titre', e.target.value)}
                          className="w-5 h-5 text-blue-500 accent-blue-500"
                          data-testid={`titre-${t.toLowerCase()}`}
                        />
                        <span className="text-white font-medium">{t}</span>
                      </label>
                    ))}
                  </div>
                  {errors.titre && (
                    <div className="flex items-center gap-1 mt-2">
                      <AlertCircle size={14} className="text-red-400" />
                      <p className="text-red-400 text-sm font-medium">Veuillez sélectionner un titre</p>
                    </div>
                  )}
                </div>
                <DateField label="Date de naissance *" value={formData.date_naissance}
                  onChange={(v) => handleChange('date_naissance', v)} error={errors.date_naissance} testId="date-naissance" />
                <InputField label="Nom *" value={formData.nom} placeholder="Votre nom"
                  onChange={(v) => handleChange('nom', v)} error={errors.nom} testId="nom" />
                <InputField label="Prénom *" value={formData.prenom} placeholder="Votre prénom"
                  onChange={(v) => handleChange('prenom', v)} error={errors.prenom} testId="prenom" />
              </div>
            </div>

            {/* Section Adresse */}
            <div className="glass p-8 rounded-2xl mb-8">
              <SectionHeader icon={MapPin} title="Adresse" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField label="Rue et numéro *" value={formData.rue} placeholder="Ex: Rue du Seyon 1"
                  onChange={(v) => handleChange('rue', v)} error={errors.rue} className="md:col-span-2" testId="rue" />
                <NpaLocaliteField label="NPA / Localité *" value={formData.npa_localite}
                  onChange={(v) => handleChange('npa_localite', v)} error={errors.npa_localite} testId="npa-localite" />
                <SelectField label="Situation de logement *" value={formData.situation_logement}
                  options={SITUATIONS_LOGEMENT}
                  onChange={(v) => handleChange('situation_logement', v)} error={errors.situation_logement} testId="situation-logement" />
                
                {formData.situation_logement && formData.situation_logement !== 'chez_parents' && (
                  <div>
                    <InputField label="Coûts de logement (y compris frais annexes) *" type="number" value={formData.loyer_mensuel}
                      placeholder="Ex: 1500" onChange={(v) => handleChange('loyer_mensuel', v)} error={errors.loyer_mensuel} testId="loyer-mensuel" />
                  </div>
                )}
                
                <MonthField label="À cette adresse depuis *" value={formData.en_menage_depuis}
                  onChange={(v) => {
                    handleChange('en_menage_depuis', v);
                    // Reset ancienne adresse si >= 6 mois
                    if (v && v.includes('-') && v.length === 7 && !isLessThan6Months(v)) {
                      handleChange('ancienne_rue', '');
                      handleChange('ancienne_npa_localite', '');
                      handleChange('ancienne_pays', 'CH');
                    }
                  }} error={errors.en_menage_depuis} testId="en-menage-depuis" />
                
                {/* Ancienne adresse si < 6 mois à l'adresse actuelle */}
                {isLessThan6Months(formData.en_menage_depuis) && (
                  <div className="md:col-span-2 mt-2">
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <AlertCircle size={18} className="text-blue-400" />
                        <span className="text-blue-300 font-medium text-sm">Vous résidez à votre adresse actuelle depuis moins de 6 mois — veuillez renseigner votre ancienne adresse.</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InputField label="Ancienne rue et numéro *" value={formData.ancienne_rue} placeholder="Ex: Löwenstrasse 52"
                          onChange={(v) => handleChange('ancienne_rue', v)} error={errors.ancienne_rue} className="md:col-span-2" testId="ancienne-rue" />
                        <NpaLocaliteField label="Ancien NPA / Localité *" value={formData.ancienne_npa_localite}
                          onChange={(v) => handleChange('ancienne_npa_localite', v)} error={errors.ancienne_npa_localite} testId="ancienne-npa-localite" />
                        <SelectField label="Pays *" value={formData.ancienne_pays}
                          options={PAYS}
                          onChange={(v) => handleChange('ancienne_pays', v)} error={errors.ancienne_pays} testId="ancienne-pays" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section Famille */}
            <div className="glass p-8 rounded-2xl mb-8">
              <SectionHeader icon={Users} title="Situation familiale" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SelectField label="État civil *" value={formData.etat_civil}
                  options={ETATS_CIVILS.map(e => ({ value: e, label: e }))}
                  onChange={(v) => handleChange('etat_civil', v)} error={errors.etat_civil} testId="etat-civil" />
                
                {/* Enfants à charge */}
                <div data-error={errors.a_enfants ? 'true' : 'false'}>
                  <label className={`block mb-2 ${errors.a_enfants ? 'text-red-400 font-medium' : 'text-gray-300'}`}>Enfants à charge (&lt;25 ans) *</label>
                  <div className={`flex gap-4 p-2 rounded-xl ${errors.a_enfants ? 'ring-2 ring-red-500/50' : ''}`}>
                    <RadioButton name="a_enfants" value={true} checked={formData.a_enfants === true}
                      onChange={() => handleChange('a_enfants', true)} label="Oui" testId="enfants-oui" />
                    <RadioButton name="a_enfants" value={false} checked={formData.a_enfants === false}
                      onChange={() => handleChange('a_enfants', false)} label="Non" testId="enfants-non" />
                  </div>
                  {errors.a_enfants && (
                    <div className="flex items-center gap-1 mt-2">
                      <AlertCircle size={14} className="text-red-400" />
                      <p className="text-red-400 text-sm font-medium">Veuillez sélectionner une option</p>
                    </div>
                  )}
                </div>
                
                {formData.a_enfants === true && (
                  <InputField label="Nombre d'enfants *" type="number" value={formData.nombre_enfants}
                    placeholder="Ex: 2" onChange={(v) => handleChange('nombre_enfants', v)} error={errors.nombre_enfants} testId="nombre-enfants" />
                )}
                
                {/* Années de naissance des enfants (requis par Cembra) */}
                {formData.a_enfants === true && parseInt(formData.nombre_enfants) > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-gray-300 mb-2">Année(s) de naissance des enfants</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Array.from({ length: Math.min(parseInt(formData.nombre_enfants) || 0, 10) }, (_, i) => (
                        <input
                          key={i}
                          type="number"
                          min="1970"
                          max={new Date().getFullYear()}
                          value={formData.enfants_annees_naissance?.[i] || ''}
                          placeholder={`Enfant ${i + 1}`}
                          onChange={(e) => handleChildYearChange(i, e.target.value)}
                          className="p-3 bg-gray-800/50 border-2 border-gray-700 rounded-xl text-white focus:border-blue-500 transition-all"
                          data-testid={`enfant-annee-${i}`}
                        />
                      ))}
                    </div>
                    <p className="text-gray-500 text-xs mt-2 italic">Requis pour la transmission à Cembra</p>
                  </div>
                )}
                
                {/* Données conjoint si marié - Section complète Cembra */}
                {(formData.etat_civil === 'Marié(e)' || formData.etat_civil === 'Partenariat enregistré') && (
                  <>
                    <div className="md:col-span-2 border-t border-gray-700 pt-4 mt-2">
                      <p className="text-blue-400 font-medium mb-4">Informations du conjoint / partenaire</p>
                    </div>
                    
                    {/* Identité conjoint */}
                    <div>
                      <label className="block text-gray-300 mb-2">Titre</label>
                      <div className="flex gap-4 p-3 rounded-xl border-2 border-gray-700 bg-gray-800/50">
                        {['Monsieur', 'Madame'].map(t => (
                          <label key={t} className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name="conjoint_titre" value={t}
                              checked={formData.conjoint_titre === t}
                              onChange={(e) => handleChange('conjoint_titre', e.target.value)}
                              className="w-5 h-5 text-blue-500 accent-blue-500"
                              data-testid={`conjoint-titre-${t.toLowerCase()}`} />
                            <span className="text-white font-medium">{t}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <DateField label="Date de naissance" value={formData.conjoint_date_naissance}
                      onChange={(v) => handleChange('conjoint_date_naissance', v)} testId="conjoint-date-naissance" />
                    <InputField label="Nom du conjoint" value={formData.conjoint_nom} placeholder="Nom"
                      onChange={(v) => handleChange('conjoint_nom', v)} testId="conjoint-nom" />
                    <InputField label="Prénom du conjoint" value={formData.conjoint_prenom} placeholder="Prénom"
                      onChange={(v) => handleChange('conjoint_prenom', v)} testId="conjoint-prenom" />

                    <div className="md:col-span-2">
                      <InputField label="E-mail du conjoint *" type="email" value={formData.conjoint_email}
                        placeholder="email.conjoint@exemple.ch"
                        onChange={(v) => handleChange('conjoint_email', v)} error={errors.conjoint_email} testId="conjoint-email" />
                      <p className="text-gray-500 text-xs mt-1 italic">
                        Votre conjoint / partenaire recevra un e-mail pour valider son consentement aux conditions de demande de crédit (obligatoire).
                      </p>
                    </div>

                    {/* Nationalité conjoint */}
                    <SelectField label="Nationalité du conjoint" value={formData.conjoint_nationalite}
                      options={NATIONALITES.map(n => ({ value: n, label: n }))}
                      onChange={(v) => {
                        handleChange('conjoint_nationalite', v);
                        if (v === 'Suisse') {
                          handleChange('conjoint_titre_sejour', 'Suisse');
                          handleChange('conjoint_en_suisse_depuis', '');
                        } else {
                          handleChange('conjoint_titre_sejour', '');
                        }
                      }} testId="conjoint-nationalite" />
                    
                    {formData.conjoint_nationalite && formData.conjoint_nationalite !== 'Suisse' && (
                      <SelectField label="Permis du conjoint" value={formData.conjoint_titre_sejour}
                        options={TITRES_SEJOUR.filter(t => t.value !== 'Suisse')}
                        onChange={(v) => handleChange('conjoint_titre_sejour', v)} testId="conjoint-titre-sejour" />
                    )}
                    {formData.conjoint_nationalite && formData.conjoint_nationalite !== 'Suisse' && formData.conjoint_titre_sejour && (
                      <MonthField label="En Suisse depuis" value={formData.conjoint_en_suisse_depuis}
                        onChange={(v) => handleChange('conjoint_en_suisse_depuis', v)} testId="conjoint-en-suisse" />
                    )}
                    
                    {/* Emploi conjoint */}
                    <div className="md:col-span-2 border-t border-gray-700/50 pt-4 mt-2">
                      <p className="text-gray-400 text-sm font-medium mb-3">Situation professionnelle du conjoint</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-gray-300 mb-2">Profil du conjoint</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {PROFILS.map(p => (
                          <label key={p.value}
                            className={`flex items-center justify-center p-3 rounded-xl cursor-pointer border-2 transition-all ${
                              formData.conjoint_profil === p.value
                                ? 'border-blue-500 bg-blue-500/20 text-white'
                                : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                            }`}>
                            <input type="radio" name="conjoint_profil" value={p.value}
                              checked={formData.conjoint_profil === p.value}
                              onChange={(e) => handleChange('conjoint_profil', e.target.value)}
                              className="sr-only"
                              data-testid={`conjoint-profil-${p.value}`} />
                            <span className="text-sm text-center">{p.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <InputField label="Employeur du conjoint" value={formData.conjoint_employeur} placeholder="Nom de l'entreprise"
                      onChange={(v) => handleChange('conjoint_employeur', v)} testId="conjoint-employeur" />
                    <MonthField label="En poste depuis" value={formData.conjoint_en_poste_depuis}
                      onChange={(v) => handleChange('conjoint_en_poste_depuis', v)} testId="conjoint-en-poste" />
                    <InputField label="NPA employeur" value={formData.conjoint_employeur_code_postal} placeholder="Ex: 2000"
                      onChange={(v) => handleChange('conjoint_employeur_code_postal', v)} testId="conjoint-employeur-npa" />
                    <InputField label="Localité employeur" value={formData.conjoint_employeur_localite} placeholder="Ex: Neuchâtel"
                      onChange={(v) => handleChange('conjoint_employeur_localite', v)} testId="conjoint-employeur-localite" />
                    
                    {/* Revenus conjoint */}
                    <div className="md:col-span-2 border-t border-gray-700/50 pt-4 mt-2">
                      <p className="text-gray-400 text-sm font-medium mb-3">Revenus et dépenses du conjoint</p>
                    </div>
                    <InputField label="Salaire net mensuel (CHF)" type="number" value={formData.conjoint_salaire}
                      placeholder="Ex: 4500" onChange={(v) => handleChange('conjoint_salaire', v)} testId="conjoint-salaire" />
                    <SelectField label="Versements par an" value={formData.conjoint_nombre_versements}
                      options={NOMBRE_VERSEMENTS}
                      onChange={(v) => handleChange('conjoint_nombre_versements', v)} testId="conjoint-versements" />
                    <InputField label="Revenus supplémentaires (CHF/mois)" type="number" value={formData.conjoint_revenus_supplementaires}
                      placeholder="Ex: allocations, rentes" onChange={(v) => handleChange('conjoint_revenus_supplementaires', v)} testId="conjoint-revenus-supp" />
                    <InputField label="Pensions alimentaires versées (CHF/mois)" type="number" value={formData.conjoint_pensions_alimentaires}
                      placeholder="Ex: 0" onChange={(v) => handleChange('conjoint_pensions_alimentaires', v)} testId="conjoint-pensions" />
                    <InputField label="Autres dettes mensuelles (CHF/mois)" type="number" value={formData.conjoint_autres_dettes}
                      placeholder="Leasing, crédits en cours..." onChange={(v) => handleChange('conjoint_autres_dettes', v)} testId="conjoint-dettes" />
                  </>
                )}
              </div>
            </div>

            {/* Section Emploi */}
            <div className="glass p-8 rounded-2xl mb-8">
              <SectionHeader icon={Briefcase} title="Emploi" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2" data-error={errors.profil ? 'true' : 'false'}>
                  <label className={`block mb-2 font-medium ${errors.profil ? 'text-red-400' : 'text-gray-300'}`}>Votre profil *</label>
                  <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 p-2 rounded-xl ${errors.profil ? 'ring-2 ring-red-500/50' : ''}`}>
                    {PROFILS.map(p => (
                      <label key={p.value} 
                        className={`flex items-center justify-center p-3 rounded-xl cursor-pointer border-2 transition-all ${
                          formData.profil === p.value 
                            ? 'border-blue-500 bg-blue-500/20 text-white' 
                            : errors.profil 
                              ? 'border-red-500/50 bg-gray-800/50 text-gray-400 hover:border-red-400'
                              : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                        }`}>
                        <input type="radio" name="profil" value={p.value}
                          checked={formData.profil === p.value}
                          onChange={(e) => handleChange('profil', e.target.value)}
                          className="sr-only"
                          data-testid={`profil-${p.value}`} />
                        <span className="text-sm text-center">{p.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.profil && (
                    <div className="flex items-center gap-1 mt-2">
                      <AlertCircle size={14} className="text-red-400" />
                      <p className="text-red-400 text-sm font-medium">{errors.profil}</p>
                    </div>
                  )}
                </div>
                
                <InputField label="Employeur *" value={formData.employeur} placeholder="Nom de l'entreprise"
                  onChange={(v) => handleChange('employeur', v)} error={errors.employeur} testId="employeur" />
                <InputField label="Profession *" value={formData.profession} placeholder="Votre poste"
                  onChange={(v) => handleChange('profession', v)} error={errors.profession} testId="profession" />
                
                {/* Adresse employeur */}
                <InputField label="NPA employeur *" value={formData.employeur_code_postal} placeholder="Ex: 2000"
                  onChange={(v) => handleChange('employeur_code_postal', v)} error={errors.employeur_code_postal} testId="employeur-npa" />
                <InputField label="Localité employeur *" value={formData.employeur_localite} placeholder="Ex: Neuchâtel"
                  onChange={(v) => handleChange('employeur_localite', v)} error={errors.employeur_localite} testId="employeur-localite" />
                
                <div>
                  <MonthField label="En poste depuis *" value={formData.en_poste_depuis}
                    onChange={(v) => handleChange('en_poste_depuis', v)} error={errors.en_poste_depuis} testId="en-poste-depuis" />
                  <p className="text-gray-500 text-xs mt-2 italic">Nous ne prenons jamais contact avec votre employeur.</p>
                </div>
                <InputField label="Salaire net mensuel (CHF) *" type="number" value={formData.salaire_net}
                  placeholder="Ex: 5500" onChange={(v) => handleChange('salaire_net', v)} error={errors.salaire_net} testId="salaire-net" />
                
                {/* Nombre de versements */}
                <SelectField label="Nombre de versements par an *" value={formData.nombre_versements_salaire}
                  options={NOMBRE_VERSEMENTS}
                  onChange={(v) => handleChange('nombre_versements_salaire', v)} error={errors.nombre_versements_salaire} testId="nombre-versements" />
                
                {/* Revenus supplémentaires */}
                <InputField label="Revenus supplémentaires nets (CHF/mois)" type="number" value={formData.revenus_supplementaires}
                  placeholder="Ex: allocations, rentes, etc." onChange={(v) => handleChange('revenus_supplementaires', v)} testId="revenus-supplementaires" />
              </div>
            </div>

            {/* Section Dépenses Détaillées */}
            <div className="glass p-8 rounded-2xl mb-8">
              <SectionHeader icon={Receipt} title="Budget mensuel détaillé" />
              <p className="text-gray-400 text-sm mb-6">Ces informations nous permettent d'évaluer précisément votre capacité de remboursement.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SelectField label="Moyen de transport pour le travail *" value={formData.moyen_transport}
                  options={MOYENS_TRANSPORT}
                  onChange={(v) => handleChange('moyen_transport', v)} error={errors.moyen_transport} testId="moyen-transport" />
                <div>
                  <InputField label="Frais de trajet travail (CHF/mois) *" type="number" value={formData.frais_trajets_travail}
                    placeholder="Transport, essence..." onChange={(v) => handleChange('frais_trajets_travail', v)} error={errors.frais_trajets_travail} testId="frais-trajets" />
                  <p className="text-gray-500 text-xs mt-1 italic">Transports publics, voiture (CHF 0.35–0.65/km), vélo</p>
                </div>
                <div>
                  <InputField label="Repas hors domicile (CHF/mois) *" type="number" value={formData.depenses_repas_exterieur}
                    placeholder="Cantines, restaurants..." onChange={(v) => handleChange('depenses_repas_exterieur', v)} error={errors.depenses_repas_exterieur} testId="repas-exterieur" />
                  <p className="text-gray-500 text-xs mt-1 italic">CHF 5 à 20 par jour de travail pour un 100%</p>
                </div>
                <InputField label="Pensions alimentaires versées (CHF/mois) *" type="number" value={formData.pensions_alimentaires}
                  placeholder="Ex: 500" onChange={(v) => handleChange('pensions_alimentaires', v)} error={errors.pensions_alimentaires} testId="pensions-alimentaires" />
                <InputField label="Primes assurance maladie (CHF/mois) *" type="number" value={formData.primes_assurance_maladie}
                  placeholder="LAMal + complémentaires" onChange={(v) => handleChange('primes_assurance_maladie', v)} error={errors.primes_assurance_maladie} testId="primes-lamal" />
                <InputField label="Impôts mensuels estimés (CHF) *" type="number" value={formData.impots_mensuels}
                  placeholder="Ex: 800" onChange={(v) => handleChange('impots_mensuels', v)} error={errors.impots_mensuels} testId="impots-mensuels" />
                <InputField label="Mensualités leasing véhicule (CHF) *" type="number" value={formData.mensualites_leasing}
                  placeholder="Ex: 350" onChange={(v) => handleChange('mensualites_leasing', v)} error={errors.mensualites_leasing} testId="mensualites-leasing" />
                <InputField label="Autres dépenses mensuelles (CHF) *" type="number" value={formData.autres_depenses_mensuelles}
                  placeholder="Autres charges fixes" onChange={(v) => handleChange('autres_depenses_mensuelles', v)} 
                  error={errors.autres_depenses_mensuelles} className="md:col-span-2" testId="autres-depenses" />
              </div>
            </div>

            {/* Section Finances */}
            <div className="glass p-8 rounded-2xl mb-8">
              <SectionHeader icon={Wallet} title="Situation financière" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-300 mb-2">Poursuites ou saisies *</label>
                  <select value={formData.poursuites} onChange={(e) => handleChange('poursuites', e.target.value)}
                    className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white"
                    data-testid="poursuites-select">
                    <option value="aucune">Aucune</option>
                    <option value="poursuite">Poursuite(s) en cours</option>
                    <option value="saisie">Saisie(s) en cours</option>
                  </select>
                </div>
                
                {formData.poursuites !== 'aucune' && (
                  <InputField label="Montant des poursuites/saisies (CHF) *" type="number"
                    value={formData.montant_poursuites} placeholder="Ex: 5000"
                    onChange={(v) => handleChange('montant_poursuites', v)} error={errors.montant_poursuites} testId="montant-poursuites" />
                )}
                
                {/* Crédit existant */}
                <div>
                  <label className="block text-gray-300 mb-2">Crédit existant à reprendre/solder ?</label>
                  <div className="flex gap-4">
                    <RadioButton name="credit_existant" value={true} checked={formData.credit_existant === true}
                      onChange={() => handleChange('credit_existant', true)} label="Oui" testId="credit-existant-oui" />
                    <RadioButton name="credit_existant" value={false} checked={formData.credit_existant === false}
                      onChange={() => handleChange('credit_existant', false)} label="Non" testId="credit-existant-non" />
                  </div>
                </div>
                
                {formData.credit_existant === true && (
                  <InputField label="Montant du crédit existant (CHF)" type="number"
                    value={formData.credit_existant_montant} placeholder="Ex: 15000"
                    onChange={(v) => handleChange('credit_existant_montant', v)} testId="credit-existant-montant" />
                )}
                
                {/* IBAN */}
                <div className="md:col-span-2">
                  <InputField label="IBAN *" value={formData.iban} placeholder="CH8000000000000000000"
                    onChange={(v) => handleChange('iban', v)} error={errors.iban} testId="iban" />
                  <p className="text-gray-500 text-xs mt-2 italic">Compte sur lequel vous souhaitez recevoir le montant du crédit en cas d'acceptation.</p>
                </div>
              </div>
            </div>

            {/* Section Nationalité */}
            <div className="glass p-8 rounded-2xl mb-8">
              <SectionHeader icon={FileText} title="Nationalité et séjour" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SelectField label="Nationalité *" value={formData.nationalite}
                  options={NATIONALITES.map(n => ({ value: n, label: n }))}
                  onChange={(v) => {
                    handleChange('nationalite', v);
                    // Si nationalité Suisse, auto-sélectionner le titre de séjour
                    if (v === 'Suisse') {
                      handleChange('titre_sejour', 'Suisse');
                      handleChange('en_suisse_depuis', '');
                    } else {
                      handleChange('titre_sejour', '');
                    }
                  }} error={errors.nationalite} testId="nationalite" />
                
                {/* Afficher le titre de séjour uniquement si nationalité non-suisse */}
                {formData.nationalite && formData.nationalite !== 'Suisse' && (
                  <SelectField label="Titre de séjour *" value={formData.titre_sejour}
                    options={TITRES_SEJOUR.filter(t => t.value !== 'Suisse')}
                    onChange={(v) => handleChange('titre_sejour', v)} error={errors.titre_sejour} testId="titre-sejour" />
                )}
                
                {/* Afficher "En Suisse depuis" uniquement pour les étrangers */}
                {formData.nationalite && formData.nationalite !== 'Suisse' && formData.titre_sejour && (
                  <MonthField label="En Suisse depuis" value={formData.en_suisse_depuis}
                    onChange={(v) => handleChange('en_suisse_depuis', v)} testId="en-suisse-depuis" />
                )}
              </div>
            </div>

            {/* Section Upload Documents */}
            {formData.profil && (
              <div className="glass p-8 rounded-2xl mb-8">
                <SectionHeader icon={Upload} title="Documents justificatifs" />
                <p className="text-gray-400 mb-4">
                  Formats acceptés : PDF, JPG, PNG, TIF, GIF, BMP (max 1 MB après compression)
                </p>
                
                {/* Checklist visuelle */}
                <div className="glass p-4 rounded-xl mb-6 border border-gray-700/50">
                  <p className="text-sm text-gray-300 font-medium mb-3">Checklist des documents :</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {documentsRequis.map(doc => {
                      const hasFile = uploadedFiles[doc.key] && uploadedFiles[doc.key].length > 0;
                      return (
                        <div key={doc.key} className="flex items-center gap-2 text-sm">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                            hasFile ? 'bg-green-500/20 text-green-400' : 'bg-red-500/10 text-red-400'
                          }`}>
                            {hasFile ? '✓' : '!'}
                          </span>
                          <span className={hasFile ? 'text-green-400' : doc.required ? 'text-gray-300' : 'text-gray-400'}>
                            {doc.label} {doc.required && !hasFile && <span className="text-red-400">*</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-4">
                  {documentsRequis.map(doc => (
                    <FileUploadField
                      key={doc.key}
                      docKey={doc.key}
                      label={doc.label}
                      required={doc.required}
                      files={uploadedFiles[doc.key]}
                      onUpload={handleFileUpload}
                      onRemove={removeFile}
                      error={errors[doc.key]}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Section Contact */}
            <div className="glass p-8 rounded-2xl mb-8">
              <SectionHeader icon={Phone} title="Contact" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField label="Téléphone *" type="tel" value={formData.telephone}
                  placeholder="Ex: 079 123 45 67" onChange={(v) => handleChange('telephone', v)} error={errors.telephone} testId="telephone" />
                <InputField label="Email *" type="email" value={formData.email}
                  placeholder="votre@email.ch" onChange={(v) => handleChange('email', v)} error={errors.email} testId="email" />
                <div className="md:col-span-2">
                  <InputField label="Conseiller / Recommandant (optionnel)" value={formData.conseiller_recommandant}
                    placeholder="Nom du conseiller ou de la personne qui vous a recommandé"
                    onChange={(v) => handleChange('conseiller_recommandant', v)} testId="conseiller-recommandant" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-300 mb-2">Commentaire (optionnel)</label>
                  <textarea value={formData.commentaire} onChange={(e) => handleChange('commentaire', e.target.value)}
                    placeholder="Informations complémentaires..." rows={3}
                    className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white resize-none"
                    data-testid="commentaire" />
                </div>
              </div>
            </div>

            {/* Consentements */}
            <div className="glass p-8 rounded-2xl mb-8">
              <div className="space-y-4">
                <Checkbox checked={formData.accept_conditions} onChange={(v) => handleChange('accept_conditions', v)}
                  error={errors.accept_conditions} testId="accept-conditions">
                  J'accepte les <Link to="/conditions-credit" className="text-blue-400 hover:underline" target="_blank">conditions de demande de crédit</Link> (notamment en lien avec la collecte et le traitement des données auprès de la Centrale d'information de crédit (ZEK) et de tiers). *
                </Checkbox>
                <Checkbox checked={formData.accept_traitement} onChange={(v) => handleChange('accept_traitement', v)}
                  error={errors.accept_traitement} testId="accept-traitement">
                  J'accepte qu'<strong>ATOM Group SA</strong> traite ma demande auprès des banques partenaires et me propose une offre personnalisée sans engagement. *
                </Checkbox>
                <Checkbox checked={formData.confirmation_exactitude_donnees} onChange={(v) => handleChange('confirmation_exactitude_donnees', v)}
                  error={errors.confirmation_exactitude_donnees} testId="confirmation-exactitude">
                  Je confirme que toutes les informations financières fournies sont exactes et complètes. Je comprends qu'une fausse déclaration peut entraîner le refus de ma demande. *
                </Checkbox>
                {(formData.etat_civil === 'Marié(e)' || formData.etat_civil === 'Partenariat enregistré') && (
                  <Checkbox checked={formData.conjoint_accept_conditions} onChange={(v) => handleChange('conjoint_accept_conditions', v)}
                    error={errors.conjoint_accept_conditions} testId="conjoint-accept-conditions">
                    Je confirme que <strong>{`${formData.conjoint_prenom} ${formData.conjoint_nom}`.trim() || 'mon conjoint / partenaire'}</strong> a
                    pris connaissance des <Link to="/conditions-credit" className="text-blue-400 hover:underline" target="_blank">conditions de demande de crédit</Link> et
                    qu'il/elle recevra un e-mail à l'adresse indiquée pour confirmer personnellement son consentement. *
                  </Checkbox>
                )}
              </div>
            </div>

            {/* Mention légale */}
            <div className="text-center mb-8">
              <p className="text-xs text-gray-500">
                Taux d'intérêt annuel entre 5.95% et 9.95%. Durée: 12-84 mois. 
                L'octroi d'un crédit est interdit s'il entraîne le surendettement du consommateur (Art. 3 LCD).
              </p>
            </div>

            {/* Bouton */}
            <div className="text-center">
              <button type="submit" disabled={isSubmitting}
                className="btn-glass btn-primary text-lg px-12 py-4 inline-flex items-center disabled:opacity-50"
                data-testid="submit-button">
                {isSubmitting ? (
                  <><Loader2 className="animate-spin mr-2" size={20} />Envoi en cours...</>
                ) : (
                  <>Envoyer ma demande<ArrowRight className="ml-2" size={20} /></>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

// Composants utilitaires
const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
      <Icon size={20} className="text-white" />
    </div>
    <h2 className="text-xl font-bold text-white">{title}</h2>
  </div>
);

const InputField = ({ label, type = 'text', value, placeholder, onChange, error, className = '', testId }) => (
  <div className={className} data-error={error ? 'true' : 'false'}>
    <label className={`block mb-2 font-medium ${error ? 'text-red-400' : 'text-gray-300'}`}>{label}</label>
    <input type={type} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full p-3 bg-gray-800/50 border-2 rounded-xl text-white transition-all ${
        error 
          ? 'border-red-500 ring-2 ring-red-500/20' 
          : 'border-gray-700 focus:border-blue-500'
      }`}
      data-testid={testId} />
    {error && (
      <div className="flex items-center gap-1 mt-2">
        <AlertCircle size={14} className="text-red-400" />
        <p className="text-red-400 text-sm font-medium">{error}</p>
      </div>
    )}
  </div>
);

// Composant Date avec format JJ/MM/AAAA
const DateField = ({ label, value, onChange, error, className = '', testId }) => {
  // Convertir YYYY-MM-DD en JJ/MM/AAAA pour l'affichage
  const formatDisplayDate = (isoDate) => {
    if (!isoDate) return '';
    const parts = isoDate.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return isoDate;
  };

  // Convertir JJ/MM/AAAA en YYYY-MM-DD pour le stockage
  const parseInputDate = (displayDate) => {
    // Nettoyer l'entrée
    const cleaned = displayDate.replace(/[^0-9]/g, '');
    
    // Auto-formater pendant la saisie
    let formatted = '';
    if (cleaned.length > 0) {
      formatted = cleaned.substring(0, 2);
      if (cleaned.length > 2) {
        formatted += '/' + cleaned.substring(2, 4);
        if (cleaned.length > 4) {
          formatted += '/' + cleaned.substring(4, 8);
        }
      }
    }
    
    // Si date complète, convertir en ISO
    if (cleaned.length === 8) {
      const day = cleaned.substring(0, 2);
      const month = cleaned.substring(2, 4);
      const year = cleaned.substring(4, 8);
      // Validation basique
      if (parseInt(day) >= 1 && parseInt(day) <= 31 && 
          parseInt(month) >= 1 && parseInt(month) <= 12 &&
          parseInt(year) >= 1900 && parseInt(year) <= 2100) {
        onChange(`${year}-${month}-${day}`);
        return;
      }
    }
    
    // Sinon, stocker tel quel pour permettre la saisie
    onChange(formatted);
  };

  const displayValue = value && value.includes('-') ? formatDisplayDate(value) : (value || '');

  return (
    <div className={className} data-error={error ? 'true' : 'false'}>
      <label className={`block mb-2 font-medium ${error ? 'text-red-400' : 'text-gray-300'}`}>{label}</label>
      <input 
        type="text" 
        value={displayValue}
        placeholder="JJ/MM/AAAA"
        onChange={(e) => parseInputDate(e.target.value)}
        maxLength={10}
        className={`w-full p-3 bg-gray-800/50 border-2 rounded-xl text-white transition-all ${
          error 
            ? 'border-red-500 ring-2 ring-red-500/20' 
            : 'border-gray-700 focus:border-blue-500'
        }`}
        data-testid={testId} 
      />
      {error && (
        <div className="flex items-center gap-1 mt-2">
          <AlertCircle size={14} className="text-red-400" />
          <p className="text-red-400 text-sm font-medium">{error}</p>
        </div>
      )}
    </div>
  );
};

// Composant Mois avec format MM/AAAA
const MonthField = ({ label, value, onChange, error, className = '', testId }) => {
  // Convertir YYYY-MM en MM/AAAA pour l'affichage
  const formatDisplayMonth = (isoMonth) => {
    if (!isoMonth) return '';
    const parts = isoMonth.split('-');
    if (parts.length === 2) {
      return `${parts[1]}/${parts[0]}`;
    }
    return isoMonth;
  };

  // Convertir MM/AAAA en YYYY-MM pour le stockage
  const parseInputMonth = (displayMonth) => {
    const cleaned = displayMonth.replace(/[^0-9]/g, '');
    
    let formatted = '';
    if (cleaned.length > 0) {
      // Limiter le mois à 12 maximum
      let monthPart = cleaned.substring(0, 2);
      if (parseInt(monthPart) > 12) {
        monthPart = '12';
      }
      if (monthPart.length === 2 && monthPart[0] !== '0' && parseInt(monthPart) > 12) {
        monthPart = '0' + monthPart[0];
      }
      formatted = monthPart;
      if (cleaned.length > 2) {
        formatted += '/' + cleaned.substring(2, 6);
      }
    }
    
    if (cleaned.length === 6) {
      let month = cleaned.substring(0, 2);
      const year = cleaned.substring(2, 6);
      // Corriger le mois s'il dépasse 12
      if (parseInt(month) > 12) {
        month = '12';
      }
      if (parseInt(month) >= 1 && parseInt(month) <= 12 &&
          parseInt(year) >= 1900 && parseInt(year) <= 2100) {
        onChange(`${year}-${month}`);
        return;
      }
    }
    
    // Ne pas envoyer de valeur invalide - envoyer vide si pas complet
    if (formatted.length === 7 && formatted.includes('/')) {
      const parts = formatted.split('/');
      if (parseInt(parts[0]) > 12) {
        onChange('');
        return;
      }
    }
    
    onChange(formatted);
  };

  const displayValue = value && value.includes('-') && value.length <= 7 ? formatDisplayMonth(value) : (value || '');

  return (
    <div className={className} data-error={error ? 'true' : 'false'}>
      <label className={`block mb-2 font-medium ${error ? 'text-red-400' : 'text-gray-300'}`}>{label}</label>
      <input 
        type="text" 
        value={displayValue}
        placeholder="MM/AAAA"
        onChange={(e) => parseInputMonth(e.target.value)}
        maxLength={7}
        className={`w-full p-3 bg-gray-800/50 border-2 rounded-xl text-white transition-all ${
          error 
            ? 'border-red-500 ring-2 ring-red-500/20' 
            : 'border-gray-700 focus:border-blue-500'
        }`}
        data-testid={testId} 
      />
      {error && (
        <div className="flex items-center gap-1 mt-2">
          <AlertCircle size={14} className="text-red-400" />
          <p className="text-red-400 text-sm font-medium">{error}</p>
        </div>
      )}
    </div>
  );
};

const SelectField = ({ label, value, options, onChange, error, testId }) => (
  <div data-error={error ? 'true' : 'false'}>
    <label className={`block mb-2 font-medium ${error ? 'text-red-400' : 'text-gray-300'}`}>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className={`w-full p-3 bg-gray-800/50 border-2 rounded-xl text-white transition-all ${
        error 
          ? 'border-red-500 ring-2 ring-red-500/20' 
          : 'border-gray-700 focus:border-blue-500'
      }`}
      data-testid={testId}>
      <option value="">Sélectionner...</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    {error && (
      <div className="flex items-center gap-1 mt-2">
        <AlertCircle size={14} className="text-red-400" />
        <p className="text-red-400 text-sm font-medium">{error}</p>
      </div>
    )}
  </div>
);

// Composant NPA/Localité avec auto-complétion
const NpaLocaliteField = ({ label, value, onChange, error, testId }) => {
  const [suggestion, setSuggestion] = useState(null);
  const [showSuggestion, setShowSuggestion] = useState(false);

  const handleInputChange = (inputValue) => {
    onChange(inputValue);
    
    // Extraire le NPA (premiers 4 chiffres)
    const npaMatch = inputValue.match(/^(\d{4})/);
    if (npaMatch) {
      const npa = npaMatch[1];
      const localite = NPA_LOCALITES[npa];
      if (localite && !inputValue.includes(localite)) {
        setSuggestion(`${npa} ${localite}`);
        setShowSuggestion(true);
      } else {
        setShowSuggestion(false);
      }
    } else {
      setShowSuggestion(false);
    }
  };

  const acceptSuggestion = () => {
    if (suggestion) {
      onChange(suggestion);
      setShowSuggestion(false);
      setSuggestion(null);
    }
  };

  return (
    <div className="relative" data-error={error ? 'true' : 'false'}>
      <label className={`block mb-2 font-medium ${error ? 'text-red-400' : 'text-gray-300'}`}>{label}</label>
      <input 
        type="text" 
        value={value}
        placeholder="Ex: 2000 Neuchâtel"
        onChange={(e) => handleInputChange(e.target.value)}
        className={`w-full p-3 bg-gray-800/50 border-2 rounded-xl text-white transition-all ${
          error 
            ? 'border-red-500 ring-2 ring-red-500/20' 
            : 'border-gray-700 focus:border-blue-500'
        }`}
        data-testid={testId} 
      />
      
      {/* Suggestion dropdown */}
      {showSuggestion && suggestion && (
        <div 
          className="absolute z-10 w-full mt-1 bg-gray-800 border border-blue-500 rounded-xl shadow-lg overflow-hidden cursor-pointer hover:bg-gray-700 transition-colors"
          onClick={acceptSuggestion}
        >
          <div className="p-3 flex items-center justify-between">
            <div>
              <span className="text-white font-medium">{suggestion}</span>
              <span className="text-gray-400 text-sm ml-2">← Cliquez pour valider</span>
            </div>
            <CheckCircle size={18} className="text-blue-400" />
          </div>
        </div>
      )}
      
      {error && (
        <div className="flex items-center gap-1 mt-2">
          <AlertCircle size={14} className="text-red-400" />
          <p className="text-red-400 text-sm font-medium">{error}</p>
        </div>
      )}
    </div>
  );
};

const Checkbox = ({ checked, onChange, error, children, testId }) => (
  <div data-error={error ? 'true' : 'false'} className={`p-3 rounded-xl border-2 transition-all ${
    error 
      ? 'border-red-500 ring-2 ring-red-500/20' 
      : 'border-transparent'
  }`}>
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className={`w-5 h-5 mt-0.5 rounded text-blue-500 accent-blue-500 ${
          error ? 'border-red-500' : 'border-gray-600'
        }`}
        data-testid={testId} />
      <span className={`text-sm ${error ? 'text-red-300' : 'text-gray-300'}`}>{children}</span>
    </label>
    {error && (
      <div className="flex items-center gap-1 mt-2 ml-8">
        <AlertCircle size={14} className="text-red-400" />
        <p className="text-red-400 text-sm font-medium">Cette case doit être cochée</p>
      </div>
    )}
  </div>
);

const RadioButton = ({ name, value, checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <input type="radio" name={name} value={value} checked={checked} onChange={onChange}
      className="w-4 h-4 text-blue-500"
      data-testid={testId} />
    <span className="text-white">{label}</span>
  </label>
);

const FileUploadField = ({ docKey, label, required, files, onUpload, onRemove, error }) => {
  const fileList = files || [];
  const hasFiles = fileList.length > 0;
  
  return (
    <div className={`p-4 rounded-xl border-2 border-dashed transition-all ${
      error 
        ? 'border-red-500 ring-2 ring-red-500/20' 
        : hasFiles 
          ? 'border-green-500/50 bg-green-500/5' 
          : 'border-gray-700 bg-gray-800/30'
    }`}
      data-error={error ? 'true' : 'false'}
      data-testid={`upload-${docKey}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <File size={20} className={`mt-1 ${hasFiles ? 'text-green-400' : error ? 'text-red-400' : 'text-gray-500'}`} />
          <div className="flex-1">
            <p className={`font-medium ${error ? 'text-red-400' : 'text-white'}`}>
              {label} {required && <span className="text-red-400">*</span>}
            </p>
            
            {/* Liste des fichiers uploadés */}
            {hasFiles && (
              <div className="mt-2 space-y-1">
                {fileList.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <span className="text-green-400">✓ {file.name}</span>
                    <span className="text-gray-500">({(file.size / 1024).toFixed(0)} KB)</span>
                    <button type="button" onClick={() => onRemove(docKey, idx)}
                      className="text-red-400 hover:text-red-300 ml-1"
                      data-testid={`remove-${docKey}-${idx}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Bouton pour ajouter des fichiers (toujours visible) */}
        <label className="cursor-pointer shrink-0">
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.gif,.bmp" className="hidden" multiple
            onChange={(e) => onUpload(docKey, e.target.files)} />
          <span className={`px-4 py-2 rounded-lg transition-colors inline-block text-sm ${
            error 
              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
              : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
          }`}>
            {hasFiles ? '+ Ajouter' : 'Choisir fichier(s)'}
          </span>
        </label>
      </div>
      
      {!hasFiles && (
        <p className={`text-xs mt-2 ${error ? 'text-red-400' : 'text-gray-500'}`}>
          {error ? 'Ce document est requis' : 'PDF, JPG, PNG • Max 1 MB • Plusieurs fichiers possibles'}
        </p>
      )}
      {error && hasFiles && (
        <div className="flex items-center gap-1 mt-2">
          <AlertCircle size={14} className="text-red-400" />
          <p className="text-red-400 text-sm font-medium">{error}</p>
        </div>
      )}
    </div>
  );
};

export default DemandeCredit;
